import { useEffect, useState } from 'react';
import {
  localDataItemService,
  localUserService,
  localReportService,
  localAnalyticsService,
  localActivityService,
  localSafetyFormService,
} from '../services/localDataService';
// Note: local service types are available in localDataService when needed

type ModelType = 'dataItems' | 'users' | 'reports' | 'analytics' | 'activities' | 'safetyForms';

/**
 * Hook for using local data service (no AWS required)
 * Perfect for local testing without AWS services
 */
export function useLocalData<T>(
  modelType: ModelType,
  options?: {
    filter?: (item: T) => boolean;
    sort?: (a: T, b: T) => number;
    limit?: number;
  }
) {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      let results: T[] = [];

      switch (modelType) {
        case 'dataItems':
          results = (await localDataItemService.getAll()) as T[];
          break;
        case 'users':
          results = (await localUserService.getAll()) as T[];
          break;
        case 'reports':
          results = (await localReportService.getAll()) as T[];
          break;
        case 'analytics':
          results = (await localAnalyticsService.getAll()) as T[];
          break;
        case 'activities':
          results = (await localActivityService.getRecent(options?.limit || 20)) as T[];
          break;
        case 'safetyForms':
          results = (await localSafetyFormService.getAll()) as T[];
          break;
      }

      // Apply filter
      if (options?.filter) {
        results = results.filter(options.filter);
      }

      // Apply sort
      if (options?.sort) {
        results = results.sort(options.sort);
      }

      // Apply limit
      if (options?.limit) {
        results = results.slice(0, options.limit);
      }

      setData(results);
    } catch (err: any) {
      setError(err);
      console.error('Error fetching local data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Poll for changes (simple approach for local testing)
    const interval = setInterval(fetchData, 2000);
    return () => clearInterval(interval);
  }, [modelType, options?.filter, options?.sort, options?.limit]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
    isOnline: navigator.onLine, // Always true for local mode
  };
}

